import { ConsentSVG } from "@/components/we360/SVGIllustrations";
import { CheckOption } from "@/components/we360/CheckOption";
import { PartyPopper, User, Heart, Moon, Pill, Activity, ShieldCheck } from "lucide-react";
import {
  calculateIMC,
  classifyIMC,
  classifyBP,
  classifyGlucose,
  calculateAge
} from "./helpers";

export default function Step7Consent({ data, setField, errors }) {
  const consentError = errors.getError("consentimiento");

  // Calculate parameters for summary
  const age = calculateAge(data.fechaNacimiento);
  const imc = calculateIMC(data.peso, data.talla);
  const imcClass = classifyIMC(imc);
  const bpClass = classifyBP(data.paSistolica, data.paDiastolica);
  const gluClass = classifyGlucose(data.glucemia);

  // Parse medications
  const meds = (() => {
    if (!data.medicamentos) return [];
    if (Array.isArray(data.medicamentos)) return data.medicamentos;
    if (typeof data.medicamentos === "string" && data.medicamentos.trim()) {
      return data.medicamentos.split(",").map((m) => ({
        nombre: m.trim(),
        dosis: "",
        codigoATC: null,
        source: "free_text",
      }));
    }
    return [];
  })();

  const renderValue = (val, unit = "") => {
    if (val === undefined || val === null || val === "") {
      return <span className="text-[#71644C]/40 font-normal">--</span>;
    }
    return (
      <span className="font-semibold text-[#2D2A24]">
        {val}
        {unit ? <span className="text-[10px] text-[#71644C]/60 font-normal ml-0.5">{unit}</span> : ""}
      </span>
    );
  };

  const SummaryField = ({ label, value, className = "" }) => (
    <div className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
      <span className="text-[9px] font-bold text-[#71644C]/60 tracking-wider uppercase">{label}</span>
      <div className="text-xs font-semibold text-[#2D2A24] flex items-center min-h-[22px] text-left">
        {value}
      </div>
    </div>
  );

  return (
    <div>
      <div className="text-center mb-4">
        <ConsentSVG accepted={data.consentimiento} />
      </div>

      {/* Ficha de Salud Summary (Bento Grid) */}
      <div className="glass-card p-5 rounded-3xl mb-5 text-left border border-white/40 shadow-sm">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#00628F] mb-4 flex items-center gap-1.5">
          <Activity size={14} />
          Ficha de Salud Integral (Resumen)
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          {/* Bloque 1: Perfil General */}
          <div className="bg-white/40 p-4 rounded-2xl border border-white/60 flex flex-col justify-between hover:shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-200">
            <div>
              <h4 className="font-bold text-[#00628F] mb-3 flex items-center gap-1.5 border-b border-[#00628F]/10 pb-2">
                <User size={13} className="text-[#00628F]" />
                Identificación y Datos
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                <SummaryField label="Usuario" value={<span className="truncate">{data.nombres || "--"} {data.apellidos || ""}</span>} className="col-span-2" />
                <SummaryField label="Edad" value={renderValue(age, "años")} />
                <SummaryField label="Sexo" value={renderValue(data.sexo)} />
                <SummaryField label="Residencia" value={renderValue(data.residencia)} className="col-span-2" />
              </div>
            </div>
          </div>

          {/* Bloque 2: Composición Corporal */}
          <div className="bg-white/40 p-4 rounded-2xl border border-white/60 flex flex-col justify-between hover:shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-200">
            <div>
              <h4 className="font-bold text-[#00628F] mb-3 flex items-center gap-1.5 border-b border-[#00628F]/10 pb-2">
                <Activity size={13} className="text-[#00628F]" />
                Antropometría
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                <SummaryField label="Peso" value={renderValue(data.peso, "kg")} />
                <SummaryField label="Talla" value={renderValue(data.talla, "cm")} />
                <SummaryField label="Cintura" value={renderValue(data.cintura, "cm")} />
                <SummaryField 
                  label="IMC" 
                  value={imc ? (
                    <span
                      className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap inline-flex items-center justify-center shadow-sm"
                      style={{ background: imcClass?.color || "#71644C" }}
                    >
                      {imc} ({imcClass?.label})
                    </span>
                  ) : (
                    <span className="text-gray-400 font-normal">--</span>
                  )} 
                />
              </div>
            </div>
          </div>

          {/* Bloque 3: Signos Vitales */}
          <div className="bg-white/40 p-4 rounded-2xl border border-white/60 flex flex-col justify-between hover:shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-200">
            <div>
              <h4 className="font-bold text-[#00628F] mb-3 flex items-center gap-1.5 border-b border-[#00628F]/10 pb-2">
                <Heart size={13} className="text-[#00628F]" />
                Signos Vitales
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                <SummaryField 
                  label="Presión Arterial" 
                  value={data.paSistolica && data.paDiastolica ? (
                    <span
                      className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap inline-flex items-center justify-center shadow-sm"
                      style={{ background: bpClass?.color || "#71644C" }}
                    >
                      {data.paSistolica}/{data.paDiastolica} mmHg ({bpClass?.label})
                    </span>
                  ) : (
                    <span className="text-gray-400 font-normal">--</span>
                  )} 
                  className="col-span-2"
                />
                <SummaryField 
                  label="Glucemia" 
                  value={data.glucemia ? (
                    <span
                      className="px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap inline-flex items-center justify-center shadow-sm"
                      style={{ background: gluClass?.color || "#71644C" }}
                    >
                      {data.glucemia} mg/dl ({gluClass?.label})
                    </span>
                  ) : (
                    <span className="text-gray-400 font-normal">--</span>
                  )} 
                  className="col-span-2"
                />
                <SummaryField label="Oximetría" value={renderValue(data.oximetria, "%")} className="col-span-2" />
              </div>
            </div>
          </div>

          {/* Bloque 4: Estilo de Vida y Meds */}
          <div className="bg-white/40 p-4 rounded-2xl border border-white/60 flex flex-col justify-between hover:shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-200">
            <div>
              <h4 className="font-bold text-[#00628F] mb-3 flex items-center gap-1.5 border-b border-[#00628F]/10 pb-2">
                <Moon size={13} className="text-[#00628F]" />
                Estilo de Vida y Medicación
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                <SummaryField label="Sueño" value={renderValue(data.sueno, "hrs/día")} />
                <SummaryField label="Tabaco" value={renderValue(data.tabaco === "Si" ? "Sí" : data.tabaco)} />
                <SummaryField label="Alcohol" value={renderValue(data.alcohol)} />
                <SummaryField 
                  label="Medicamentos" 
                  value={meds.length > 0 ? (
                    <div className="flex flex-wrap gap-1 max-w-full">
                      {meds.map((m, i) => (
                        <span
                          key={i}
                          className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-[#E8F4F8] text-[#00628F] border border-sky-100 whitespace-nowrap inline-block shadow-sm"
                        >
                          {m.nombre} {m.dosis && `(${m.dosis})`}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[#71644C] font-semibold">{data.usaMedicamentos === "No" ? "Ninguno" : "--"}</span>
                  )} 
                  className="col-span-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Acuerdo de Consentimiento */}
      <div
        className="rounded-3xl p-5 mb-5 text-xs leading-relaxed border border-white/40 shadow-sm glass-card text-left"
        style={{
          background: "rgba(255, 255, 255, 0.4)",
        }}
        data-testid="consent-text"
      >
        Declaro que he leído y acepto participar voluntariamente en el programa
        piloto <strong>WE 360</strong>, entendiendo que mis datos serán tratados de forma{" "}
        <strong>confidencial</strong> y exclusivamente con fines de{" "}
        <strong>salud y bienestar comunitario</strong>.
        <br /><br />
        Consulta nuestro{" "}
        <a href="https://saluddigital360.com/acuerdo-usuario" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#00628F] hover:underline">
          acuerdo de usuario
        </a>{" "}
        y nuestra{" "}
        <a href="https://saluddigital360.com/politicas-privacidad" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#00628F] hover:underline">
          política de privacidad
        </a>.
      </div>

      <div className="mb-4">
        <CheckOption
          label="Acepto el consentimiento informado"
          checked={data.consentimiento}
          onChange={() => setField("consentimiento", !data.consentimiento)}
          testId="check-consentimiento"
        />
      </div>

      {consentError && (
        <p className="text-xs mt-2 font-semibold text-[#C0453A] text-left" data-testid="consent-error">
          {consentError}
        </p>
      )}

      {data.consentimiento && (
        <div
          className="rounded-2xl p-4 mt-3 text-center flex items-center justify-center gap-2.5 border border-[#A3E2C9]"
          style={{ background: "#E2F0E8" }}
          data-testid="consent-success-message"
        >
          <PartyPopper size={18} color="#157346" />
          <span className="text-[13px] font-bold text-[#157346]">
            ¡Todo listo! Envía tu registro para continuar.
          </span>
        </div>
      )}
    </div>
  );
}

